import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang, loc } from "../i18n/LanguageContext";
import { Icon, LazyImg } from "./common";
import { fileUrl } from "../lib/api";

export const StarRow = ({ n = 5, size = 15 }) => (
  <div className="flex gap-0.5 text-amber-400">
    {Array.from({ length: 5 }).map((_, i) => (
      <Icon key={i} name="Star" size={size} fill={i < n ? "currentColor" : "none"} className={i < n ? "" : "text-slate-300"} />
    ))}
  </div>
);

export const ServiceCard = ({ svc, index = 0 }) => {
  const { lang, t } = useLang();
  const name = loc(svc, "name", lang);
  const desc = loc(svc, "desc", lang);
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.55, delay: (index % 4) * 0.06, ease: [0.2, 0.8, 0.2, 1] }}
      className="group relative overflow-hidden rounded-3xl bg-white border border-black/5 hover-lift" data-testid={`service-card-${svc.slug}`}
    >
      <Link to={`/services/${svc.slug}`} className="block">
        <div className="relative h-44 overflow-hidden">
          <LazyImg src={fileUrl(svc.image)} alt={name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-navy/10 to-transparent" />
          <div className="absolute top-3 start-3 grid place-items-center h-10 w-10 rounded-full glass text-blue">
            <Icon name={svc.icon || "Sparkles"} size={18} />
          </div>
        </div>
        <div className="p-6">
          <h3 className="font-display text-lg text-navy leading-snug">{name}</h3>
          <p className="mt-2 text-sm text-slate-500 leading-relaxed line-clamp-2">{desc}</p>
          <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-blue">
            {t.cta.details} <Icon name="ArrowRight" size={15} className="transition-transform group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
          </span>
        </div>
      </Link>
    </motion.div>
  );
};

export const ServiceTextCard = ({ svc, index = 0 }) => {
  const { lang, t } = useLang();
  const name = loc(svc, "name", lang);
  const desc = loc(svc, "desc", lang);
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-30px" }}
      transition={{ duration: 0.4, delay: (index % 6) * 0.04 }}
      data-testid={`service-card-${svc.slug}`}
    >
      <Link to={`/services/${svc.slug}`}
        className="group flex h-full items-start gap-4 rounded-2xl bg-white border border-black/5 p-5 hover:border-blue hover:bg-blue/[0.04] transition-colors">
        <div className="grid place-items-center h-11 w-11 rounded-xl bg-blue/10 text-blue shrink-0 group-hover:bg-blue group-hover:text-white transition-colors">
          <Icon name={svc.icon || "Sparkles"} size={19} />
        </div>
        <div className="min-w-0">
          <h3 className="font-display text-base text-navy leading-snug">{name}</h3>
          <p className="mt-1 text-sm text-slate-500 leading-relaxed line-clamp-2">{desc}</p>
          <span className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-blue">
            {t.cta.details} <Icon name="ArrowRight" size={14} className="transition-transform group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
          </span>
        </div>
      </Link>
    </motion.div>
  );
};

export const ProjectCard = ({ p, index = 0 }) => (  <motion.div
    initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
    transition={{ duration: 0.55, delay: (index % 3) * 0.08 }}
    className="group relative overflow-hidden rounded-3xl hover-lift" data-testid="project-card"
  >
    <div className="relative aspect-[4/5] overflow-hidden">
      <LazyImg src={fileUrl(p.image)} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/30 to-transparent opacity-90" />
    </div>
    <div className="absolute inset-x-0 bottom-0 p-6">
      <span className="inline-block rounded-full bg-blue px-3 py-1 text-xs font-semibold text-white">{p.category}</span>
      <h3 className="mt-3 font-display text-xl text-white leading-snug">{p.title}</h3>
      <p className="mt-2 text-sm text-white/60 leading-relaxed line-clamp-2 opacity-0 max-h-0 group-hover:opacity-100 group-hover:max-h-24 transition-all duration-500">{p.summary}</p>
    </div>
  </motion.div>
);
